import { Directive, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { LazyLoadEvent } from 'primeng/api';
import { EMPTY, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { PageRequest } from 'src/app/admihair/api/dto/paginacao/page-request';
import { PageResponse } from 'src/app/admihair/api/dto/paginacao/page-response';

@Directive()
export abstract class DialogPaginadoBaseComponent<T> implements OnInit, OnChanges, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private readonly busca$ = new Subject<string>();

  loading = false;
  first = 0;
  valorFiltro = '';
  itens: T[] = [];
  respostaPaginada?: PageResponse<T>;
  campoOrdenacao = '';
  ordemOrdenacao: 1 | -1 = 1;

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  pageRequest: PageRequest = {
    page: 0,
    size: 5,
    sort: ''
  };

  ngOnInit(): void {
    this.configurarBusca();
    this.resetarEstado();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.resetarEstado();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLazyLoad(event: LazyLoadEvent) {
    if (event.first !== undefined && event.rows !== undefined) {
      this.first = event.first;
      this.pageRequest.size = event.rows || this.quantidadePadraoPorPagina;
      this.pageRequest.page = event.rows ? Math.floor(event.first / event.rows) : 0;
    }

    this.definirOrdenacao(event.sortField, event.sortOrder);
    this.carregarItens();
  }

  buscar(event: Event) {
    this.busca$.next((event.target as HTMLInputElement).value ?? '');
  }

  limpar() {
    this.resetarEstado();
    this.carregarItens();
  }

  fechar() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  protected resetarEstado() {
    this.valorFiltro = '';
    this.itens = [];
    this.respostaPaginada = undefined;
    this.first = 0;
    this.pageRequest = this.criarPageRequestInicial();

    this.aplicarOrdenacaoVisual(this.ordenacaoPadrao);
  }

  protected carregarItens() {
    if (!this.visible) {
      this.limparResultado();
      this.loading = false;
      return;
    }

    this.loading = true;
    this.buscarPagina(this.valorFiltro || undefined, this.pageRequest).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe({
      next: (data) => {
        this.atualizarResultado(data);
      },
      error: (error) => {
        this.tratarErroCarregamento(error);
      }
    });
  }

  protected get quantidadePadraoPorPagina(): number {
    return 5;
  }

  protected abstract buscarPagina(busca: string | undefined, pageRequest: PageRequest): Observable<PageResponse<T>>;
  protected abstract get ordenacaoPadrao(): string;

  protected tratarErroCarregamento(error: unknown) {
    this.limparResultado();
    console.error('Erro ao carregar dialog paginado.', error);
  }

  protected criarPageRequestInicial(): PageRequest {
    return {
      page: 0,
      size: this.quantidadePadraoPorPagina,
      sort: this.ordenacaoPadrao
    };
  }

  private configurarBusca() {
    this.busca$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap((value) => {
        this.valorFiltro = value;
        this.first = 0;
        this.pageRequest.page = 0;
      }),
      switchMap(() => {
        if (!this.visible) {
          this.limparResultado();
          return EMPTY;
        }

        this.loading = true;
        return this.buscarPagina(this.valorFiltro || undefined, this.pageRequest).pipe(
          finalize(() => {
            this.loading = false;
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        this.atualizarResultado(data);
      },
      error: (error) => {
        this.loading = false;
        this.tratarErroCarregamento(error);
      }
    });
  }

  private definirOrdenacao(sortField?: string | string[], sortOrder?: number) {
    const campo = Array.isArray(sortField) ? sortField[0] : sortField;

    if (!campo) {
      this.pageRequest.sort = this.ordenacaoPadrao;
      this.aplicarOrdenacaoVisual(this.ordenacaoPadrao);
      return;
    }

    const direcao = sortOrder === -1 ? 'desc' : 'asc';
    this.pageRequest.sort = `${campo},${direcao}`;
    this.campoOrdenacao = campo;
    this.ordemOrdenacao = sortOrder === -1 ? -1 : 1;
  }

  private aplicarOrdenacaoVisual(sort?: string) {
    if (!sort) {
      this.campoOrdenacao = '';
      this.ordemOrdenacao = 1;
      return;
    }

    const [campo, direcao] = sort.split(',');
    this.campoOrdenacao = campo;
    this.ordemOrdenacao = direcao === 'desc' ? -1 : 1;
  }

  private atualizarResultado(data?: PageResponse<T>) {
    this.respostaPaginada = data;
    this.itens = data?.content ?? [];
  }

  private limparResultado() {
    this.itens = [];
    this.respostaPaginada = undefined;
  }

}
